<template>
  <div class="container mb-16">
    <div class="flex justify-between mb-4">
      <Button variant="ghost" size="icon" class="border rounded-lg" @click="goBack">
        <ArrowLeft class="h-6 w-6" />
      </Button>
    </div>
    <div class="flex justify-center" v-if="isLoading">
      <CustomSkeleton />
    </div>
    <OrderDetailsCard
      v-if="!isLoading"
      :merchant-name="order?.merchantName ?? ''"
      :reference="order?.reference ?? ''"
      :image-url="order?.imageUrl ?? ''"
      :progress="progress"
      :remaining-time="remainingTime"
      :show-overlay="showOverlay"
      :is-mobile="isMobileOrIsTable"
      :progress-text-class="progressTextClass"
      :isPayAtEnabled="isPayAtEnabled"
      :payAtReference="paymentPlan?.payAtReferenceNumber"
    >
      <template #desktop-badge>
        <Badge
          :class="[
            'rounded-full ml-2 text-[10px]',
            isSuspended
              ? 'text-orange-700 border-orange-200 bg-orange-50'
              : 'text-error-600 border-error-200 bg-error-50',
          ]"
          variant="outline"
          v-if="isCancelled || isCancelling || isSuspended"
        >
          {{ isCancelling ? 'Cancellation Requested' : isCancelled ? 'Cancelled' : 'Suspended' }}
        </Badge>
      </template>

      <template #mobile-fields>
        <div class="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Target</p>
            <p class="text-[18px] font-extrabold" :class="valueTextClass">{{ displayCurrency(orderTotal) }}</p>
          </div>
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Contributed</p>
            <p class="text-[18px] font-semibold" :class="valueTextClass">{{ displayCurrency(paidAmount) }}</p>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Outstanding</p>
            <p class="text-[18px] font-semibold" :class="valueTextClass">{{ displayCurrency(orderBalance) }}</p>
          </div>
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Monthly amount</p>
            <p class="text-[18px] font-semibold" :class="valueTextClass">{{ displayCurrency(monthlyAmount) }}</p>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Next instalment</p>
            <p class="text-[18px] font-semibold" :class="valueTextClass">{{ nextInstalmentDate }}</p>
          </div>
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Final payment date</p>
            <p class="text-[18px] font-semibold" :class="valueTextClass">{{ finalPaymentDate }}</p>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4 mb-6">
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Duration</p>
            <p class="text-[18px] font-semibold" :class="valueTextClass">{{ duration }}</p>
          </div>
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Order status</p>
            <p class="text-[18px] font-semibold" :class="valueTextClass">{{ capitalizedOrderState }}</p>
          </div>
        </div>
      </template>

      <template #desktop-fields>
        <div class="grid grid-cols-4 gap-6">
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Target</p>
            <p class="text-[22px] font-extrabold" :class="valueTextClass">{{ displayCurrency(orderTotal) }}</p>
          </div>
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Contributed</p>
            <p class="text-[20px] font-semibold" :class="valueTextClass">{{ displayCurrency(paidAmount) }}</p>
          </div>
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Outstanding</p>
            <p class="text-[20px] font-semibold" :class="valueTextClass">{{ displayCurrency(orderBalance) }}</p>
          </div>
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Monthly amount</p>
            <p class="text-[20px] font-semibold" :class="valueTextClass">{{ displayCurrency(monthlyAmount) }}</p>
          </div>
        </div>

        <div class="grid grid-cols-4 gap-6">
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Next instalment</p>
            <p class="text-[18px] font-semibold" :class="valueTextClass">{{ nextInstalmentDate }}</p>
          </div>
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Final payment date</p>
            <p class="text-[18px] font-semibold" :class="valueTextClass">{{ finalPaymentDate }}</p>
          </div>
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Duration</p>
            <p class="text-[18px] font-semibold" :class="valueTextClass">{{ duration }}</p>
          </div>
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Order status</p>
            <p class="text-[18px] font-semibold" :class="valueTextClass">{{ capitalizedOrderState }}</p>
          </div>
        </div>
      </template>

      <template #mobile-actions>
        <div class="flex flex-col gap-3 w-full">
          <div
            v-if="isCancelled || isCancelling || isSuspended"
            :class="[
              'w-full border-[1px] h-[40px] rounded-[9999px] flex justify-center items-center px-2',
              isSuspended
                ? 'bg-orange-50 text-orange-700 hover:bg-orange-50 border-orange-200'
                : 'bg-error-50 text-error-700 hover:bg-error-50 border-error-200',
            ]"
          >
            {{ isCancelling ? 'Cancellation Requested' : isCancelled ? 'Cancelled' : 'Suspended' }}
          </div>
          <Button
            v-if="
              !isCancelled && !isCancelling && !isSuspendedByCancellation && !isCompleted && !isPartial && !isExpired
            "
            variant="outline"
            class="border-error-200 text-error-700 hover:text-error-700 w-full"
            data-testid="cancel-order-btn"
            @click="cancelOrder"
            >{{ isSuspended ? 'Cancel' : 'Request Cancellation' }}</Button
          >
          <Button
            data-testid="make-payment-btn"
            class="bg-teal-600 hover:bg-teal-700 w-full ml-auto disabled:bg-grey-light-400"
            :disabled="!isPlacedOrPartial || isCompleted"
            @click="showPaymentModal = true"
            >Make payment</Button
          >
        </div>
      </template>

      <template #desktop-actions>
        <Button
          v-if="!isCancelled && !isCancelling && !isSuspendedByCancellation && !isCompleted && !isPartial && !isExpired"
          variant="outline"
          class="border-error-200 text-error-700 hover:text-error-700"
          data-testid="cancel-order-btn"
          @click="cancelOrder"
          >{{ isSuspended ? 'Cancel' : 'Request Cancellation' }}</Button
        >
        <Button
          data-testid="make-payment-btn"
          class="bg-teal-600 hover:bg-teal-700 disabled:bg-grey-light-400"
          @click="showPaymentModal = true"
          :disabled="!isPlacedOrPartial || isCompleted"
          >Make payment</Button
        >
      </template>
    </OrderDetailsCard>

    <div v-if="!isLoading" class="space-y-6">
      <ProductsCard :items="productItems" />
      <StatementCard :payments="payments" :order="order" />
      <NotificationsCard :order="order" @reply="showCancellationDialog = true" />
    </div>
  </div>

  <Dialog :open="showPaymentModal" @update:open="onDialogUpdate">
    <DialogContent data-testid="contribution-modal" :class="dialogWidthClass" preventClose>
      <ContributeModal
        v-if="order"
        :order="order"
        :upcoming="upcoming"
        @close="onContributeClose"
        @completed="onPaymentCompleted"
        @step-change="contributeStep = $event"
      />
    </DialogContent>
  </Dialog>

  <CancellationRequestDialog
    :open="showCancellationDialog"
    :order="order"
    @update:open="showCancellationDialog = $event"
    @cancelled="onCancellationComplete"
  />
</template>

<script setup lang="ts">
import CustomSkeleton from '@/components/common/CustomSkeleton.vue';
import ContributeModal from '@/components/shopper/contribute/ContributeModal.vue';
import CancellationRequestDialog from '@/components/shopper/lay-by/cancellation-request/CancellationRequest.vue';
import NotificationsCard from '@/components/shopper/lay-by/cancellation-request/NotificationsCard.vue';
import ProductsCard from '@/components/shopper/lay-by/ProductsCard.vue';
import OrderDetailsCard from '@/components/shopper/OrderDetailsCard.vue';
import StatementCard from '@/components/shopper/StatementCard.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import type { IPaymentReport } from '@/models/payments';
import { getOrderByIdWithPlansAndPayments } from '@/services/orders';
import { useOrderStore } from '@/stores/orders';
import { displayCurrency } from '@/utils/format.utils';
import { addMetric, createException } from '@/utils/monitoring.utils';
import { paymentReports, upcomingPayments } from '@/utils/paymentPlan.utils';
import { useBreakpoint } from '@/utils/useBreakPoint';
import dayjs from 'dayjs';
import { capitalize } from 'lodash';
import { ArrowLeft } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { ModelOrderResponse } from '@layup/api-sdk';

const route = useRoute();
const router = useRouter();
const order = ref<ModelOrderResponse>();
const payments = ref<IPaymentReport[]>([]);
const showPaymentModal = ref(false);
const showCancellationDialog = ref(false);
const contributeStep = ref<string>('');

const dialogWidthClass = computed(() => [
  'w-[calc(100vw-1rem)] sm:w-full max-h-[90vh] overflow-y-auto transition-all duration-300 rounded-xl p-3 sm:p-6',
  contributeStep.value === 'debi-check'
    ? 'sm:max-w-[640px]'
    : contributeStep.value === 'select-method' || contributeStep.value === 'status'
      ? 'sm:max-w-[560px]'
      : 'sm:max-w-[420px]',
]);

const { isMobile, isTablet } = useBreakpoint();

const isMobileOrIsTable = computed(() => isMobile.value || isTablet.value);

const orderBalance = computed(() => order.value?.balance ?? 0);
const orderTotal = computed(() => order.value?.amountDue ?? 0);
const paidAmount = computed(() => orderTotal.value - orderBalance.value);
const isCancelled = computed(() => order.value?.state === 'CANCELLED');
const isCancelling = computed(() => order.value?.state === 'CANCELLING');
const isSuspended = computed(() => order.value?.state === 'SUSPENDED');
const isCompleted = computed(() => order.value?.state === 'COMPLETED');
const isPartial = computed(() => order.value?.state === 'PARTIAL');
const isExpired = computed(() => order.value?.state === 'EXPIRED');
const isSuspendedByCancellation = computed(() => {
  if (!isSuspended.value) return false;

  const request = order.value?.orderCancellationRequest;

  return !!order.value?.suspendOnCancel && request === 'ACCEPTED';
});
const showOverlay = computed(() => isCancelled.value);
const isPlacedOrPartial = computed(
  () => order.value?.state === 'PLACED' || order.value?.state === 'PARTIAL' || !isSuspendedByCancellation.value,
);
const isLoading = ref(true);

const progress = computed(() => {
  if (!orderTotal.value) return 0;
  return Math.round((paidAmount.value / orderTotal.value) * 100);
});

const monthlyAmount = computed(() => {
  if (!order.value?.plans?.[0]?.payments) return 0;
  const payments = order.value.plans[0].payments.filter((p) => !p.paid);
  return payments[0]?.amount ?? 0;
});

const nextInstalmentDate = computed(() => {
  if (!order.value?.plans?.[0]?.payments) return '-';
  const nextPayment = order.value.plans[0].payments.find((p) => !p.paid);
  return nextPayment ? dayjs(nextPayment.dueAt || nextPayment.timestamp).format('YYYY-MM-DD') : '-';
});

const finalPaymentDate = computed(() => dayjs(order.value?.endDateMax).format('YYYY-MM-DD'));

const duration = computed(() => {
  const quantity = order.value?.plans?.[0]?.quantity ?? 0;
  const frequency = order.value?.plans?.[0]?.frequency;
  const unit = frequency === 'WEEKLY' ? 'week' : 'month';
  return `${quantity} ${quantity > 1 ? `${unit}s` : unit}`;
});

const capitalizedOrderState = computed(() => capitalize(order.value?.state ?? ''));

const upcoming = computed(() => {
  const plan = order.value?.plans?.[0];
  return plan ? upcomingPayments(plan as any) : [];
});

const remainingTime = computed(() => {
  if (!order.value?.plans?.[0]?.payments) return '0 months remaining';
  const remaining = Math.max(0, order.value.plans[0].payments.filter((p) => !p.paid && !p.pending).length);
  const frequency = order.value.plans[0].frequency;
  const unit = frequency === 'WEEKLY' ? 'week' : 'month';
  return `${remaining} ${remaining > 1 ? `${unit}s` : unit} remaining`;
});

const valueTextClass = computed(() => (isCancelled.value ? 'text-grey-light-300' : 'text-brand-700'));
const progressTextClass = computed(() => (isCancelled.value ? 'text-grey-light-300' : 'text-brand-600'));

const paymentPlan = computed(() => order.value?.plans?.[0]);

const isPayAtEnabled = computed(() => order.value?.paymentMethods?.payAt!);

const productItems = computed(() => {
  if (!order.value?.products) return [];

  return order.value.products.map((item) => ({
    id: item._id ?? '',
    description: item.name ?? '',
    amount: item.amount ?? 0,
    state: order.value?.state ?? '',
  }));
});

const init = async () => {
  try {
    const orderId = route.params.id as string;
    if (orderId) {
      isLoading.value = true;
      order.value = await getOrderByIdWithPlansAndPayments(orderId);
      if (order.value?.plans) {
        payments.value = paymentReports(order.value?.state ?? '', order.value.plans[0]);
      }
      addMetric('view_order_details', { channel: 'shopper', orderId, orderType: 'layby' });
    }

    window.scrollTo(0, 0);
  } catch (error) {
    createException(error);
  } finally {
    isLoading.value = false;
  }
};

const cancelOrder = () => {
  showCancellationDialog.value = true;
};

const onCancellationComplete = async () => {
  await init();
  useOrderStore().fetchAllOrders(true);
};

const onContributeClose = () => {
  showPaymentModal.value = false;
  if (contributeStep.value === 'status') {
    init();
  }
};

const onDialogUpdate = (open: boolean) => {
  if (!open) {
    onContributeClose();
  }
};

const onPaymentCompleted = () => {
  showPaymentModal.value = false;
  contributeStep.value = '';
  addMetric('contribute_completed', { channel: 'shopper', orderId: order.value?._id, orderType: 'layby' });
  init();
};

const goBack = () => {
  router.back();
};

init();
</script>
